import React, { useState, useCallback, useEffect } from 'react'
import {
  Box, Flex, Text, Input, Button, HStack, Tooltip, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  useDisclosure, Badge, Spinner, IconButton, Center, SimpleGrid,
} from '@chakra-ui/react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Mail, Download, Copy, Eye, Save, FileText, ArrowLeft, LayoutTemplate, ShieldCheck } from 'lucide-react'
import Header from '../../../components/Header'
import EmailDesigner from '../../TemplateManager/CreateTemplate/EmailDesigner/EmailDesigner'
import useEmailDesignerStore from '../../TemplateManager/CreateTemplate/EmailDesigner/emailDesignerStore'
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper'
import { fetchWrapper } from '../../../helpers'
import { decodeHtmlEntities, decodeBlocksContent } from '../../TemplateManager/CreateTemplate/EmailDesigner/htmlExport'
import { blocksToAmpEmail } from '../../TemplateManager/CreateTemplate/EmailDesigner/ampEmailExport'
import SendTestEmailModal from '../../TemplateManager/CreateTemplate/SendTestEmailModal'
import EmailLintModal from '../../TemplateManager/CreateTemplate/EmailDesigner/EmailLintModal'
import { PRIMARY_BG } from '../../../theme/brand';
import API from '../../../helpers/axiosInstance'
import { getApiHeaders, hasPermission } from '../../../utils/DefaultFunction'

const ACCENT = PRIMARY_BG

// Walks the block tree (including nested column blocks) looking for a button with no label
function hasEmptyButtonLabel(blockList) {
  for (const block of blockList) {
    if (block.type === 'button' && !block.props?.label?.trim()) return true
    if (block.type === 'columns' && block.props?.columnDefs) {
      for (const col of block.props.columnDefs) {
        if (hasEmptyButtonLabel(col.blocks || [])) return true
      }
    }
  }
  return false
}

function PreviewModal({ isOpen, onClose, html }) {
  const hasContent = Boolean(html && html.trim())
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent h="92vh" display="flex" flexDir="column" borderRadius="16px" overflow="hidden">
        <ModalHeader
          fontSize="13px" fontWeight="700" py={3} px={5}
          bg="gray.50" borderBottom="1px solid" borderBottomColor="gray.100"
        >
          <HStack spacing={3}>
            <Box
              w={6} h={6} borderRadius="6px"
              style={{ background: `${ACCENT}15` }}
              display="flex" alignItems="center" justifyContent="center"
            >
              <Eye size={13} color={ACCENT} />
            </Box>
            <Text color="gray.700">Email Preview</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={2.5} right={4} />
        <ModalBody p={0} flex={1} overflow="hidden" bg="gray.50">
          {isOpen && hasContent ? (
            <iframe
              key={html.length}
              srcDoc={html}
              title="Email Preview"
              style={{ display: 'block', width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <Center h="100%">
              <Text fontSize="13px" color="gray.400">Add some blocks to preview your email.</Text>
            </Center>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function TemplatesLibraryModal({ isOpen, onClose, onUse }) {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [templates, setTemplates] = useState([])
  const [applyingId, setApplyingId] = useState(null)

  useEffect(() => {
    if (isOpen) fetchTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  async function fetchTemplates() {
    setLoading(true)
    try {
      const url = `${process.env.REACT_APP_API_URL}/email-template/list?page=1&limit=100&is_template=true`
      const res = await fetchWrapper.get(url)
      const list = (res?.data || []).filter(t => t.is_template)
      setTemplates(list)
    } catch (error) {
      toast({ title: 'Failed to load templates', status: 'error', duration: 3000 })
    } finally {
      setLoading(false)
    }
  }

  async function handleUse(templateItem) {
    setApplyingId(templateItem.id)
    try {
      const url = `${process.env.REACT_APP_API_URL}/email-template/get-by-id?id=${templateItem.id}`
      const res = await fetchWrapper.get(url)
      const item = res?.data || templateItem
      onUse(item)
      onClose()
    } catch (error) {
      toast({ title: 'Failed to load template', status: 'error', duration: 3000 })
    } finally {
      setApplyingId(null)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent h="82vh" display="flex" flexDir="column" borderRadius="16px" overflow="hidden">
        <ModalHeader
          fontSize="13px" fontWeight="700" py={3} px={5}
          bg="gray.50" borderBottom="1px solid" borderBottomColor="gray.100"
        >
          <HStack spacing={3}>
            <Box
              w={6} h={6} borderRadius="6px"
              style={{ background: '#7c3aed15' }}
              display="flex" alignItems="center" justifyContent="center"
            >
              <LayoutTemplate size={13} color="#7c3aed" />
            </Box>
            <Text color="gray.700">Choose a Template</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={2.5} right={4} />
        <ModalBody p={5} flex={1} overflowY="auto" bg="gray.50">
          {loading ? (
            <Center h="100%"><Spinner size="md" color={ACCENT} /></Center>
          ) : templates.length === 0 ? (
            <Center h="100%">
              <Text fontSize="13px" color="gray.400">No templates saved yet. Save one with "Save Template".</Text>
            </Center>
          ) : (
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
              {templates.map(t => {
                const hasThumb = Boolean(t.html_code && t.html_code.trim())
                return (
                  <Box
                    key={t.id}
                    borderRadius="12px"
                    border="1px solid"
                    borderColor="gray.150"
                    bg="white"
                    overflow="hidden"
                    _hover={{ borderColor: `${ACCENT}55`, shadow: '0 4px 14px rgba(0,0,0,0.08)' }}
                    transition="all 0.15s"
                  >
                    {/* Mini live preview */}
                    <Box position="relative" h="150px" bg="gray.100" overflow="hidden" borderBottom="1px solid" borderColor="gray.100">
                      {hasThumb ? (
                        <div
                          style={{
                            width: '400%',
                            height: '600px',
                            transform: 'scale(0.25)',
                            transformOrigin: 'top left',
                          }}
                        >
                          <iframe
                            srcDoc={decodeHtmlEntities(t.html_code)}
                            title={t.name}
                            tabIndex={-1}
                            style={{ width: '100%', height: '100%', border: 'none', display: 'block', pointerEvents: 'none' }}
                          />
                        </div>
                      ) : (
                        <Center h="100%">
                          <FileText size={22} color="#c7cad1" />
                        </Center>
                      )}
                      {/* Fade so cut-off content looks intentional */}
                      <Box position="absolute" bottom={0} left={0} right={0} h="28px" style={{ background: 'linear-gradient(transparent, rgba(255,255,255,0.9))' }} />
                    </Box>

                    <Box p={3}>
                      <Text fontSize="12.5px" fontWeight="700" color="gray.800" noOfLines={1} mb={0.5}>{t.name}</Text>
                      <Text fontSize="10.5px" color="gray.400" mb={2.5}>
                        {Array.isArray(t.variables) ? t.variables.length : 0} variable{Array.isArray(t.variables) && t.variables.length === 1 ? '' : 's'}
                      </Text>
                      <Button
                        size="sm" h="28px" w="100%"
                        fontSize="11.5px" fontWeight="700"
                        isLoading={applyingId === t.id}
                        style={{ background: ACCENT, color: 'white' }}
                        _hover={{ opacity: 0.9 }}
                        onClick={() => handleUse(t)}
                      >
                        Use
                      </Button>
                    </Box>
                  </Box>
                )
              })}
            </SimpleGrid>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

function EmailDesignerPage() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()

  const [templateName, setTemplateName] = useState('Untitled Email')
  const [currentHtml, setCurrentHtml] = useState('')
  const [isSaved, setIsSaved] = useState(false)
  const [isLoading, setIsLoading] = useState(isEdit)
  const [isSaving, setIsSaving] = useState(false)
  const [loadedIsTemplate, setLoadedIsTemplate] = useState(false)
  const toast = useToast()
  const { isOpen: previewOpen, onOpen: openPreview, onClose: closePreview } = useDisclosure()
  const { isOpen: templatesOpen, onOpen: openTemplates, onClose: closeTemplates } = useDisclosure()
  const { isOpen: testEmailOpen, onOpen: openTestEmail, onClose: closeTestEmail } = useDisclosure()
  const { isOpen: lintOpen, onOpen: openLint, onClose: closeLint } = useDisclosure()
  const {
    blocks, emailSettings, getUsedVariables,
    setBlocks, setEmailSettings, setCustomVariables, clearAll,
  } = useEmailDesignerStore()

  const location = useLocation()
  const [featurePermissions, setFeaturePermissions] = useState([])
  const [featurePermissionsLoading, setFeaturePermissionsLoading] = useState(true)
  // Backend groups email-template permissions under the list page's own feature
  // slug, not this editor's route (which includes the template id when editing).
  const permParams = "tools/email-templates"

  const fetchPermissions = async () => {
    setFeaturePermissionsLoading(true)
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`
      const response = await API.get(url, { headers: getApiHeaders() })
      setFeaturePermissions(response.data.data || [])
    } catch (error) {
      console.error("Error fetching permissions:", error)
      setFeaturePermissions([])
    } finally {
      setFeaturePermissionsLoading(false)
    }
  }
  useEffect(() => {
    fetchPermissions()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Whether the current user has email-template-create/email-template-edit
  // permission for this record — when false, the canvas stays visible
  // (view-only) and only the Save actions are hidden, rather than blocking
  // the whole page.
  const canSaveEmailTemplate = hasPermission(isEdit ? "email-template-edit" : "email-template-create", featurePermissions)
  const emailTemplateViewPermission = hasPermission("email-template-view", featurePermissions)
  const emailTemplateListPermission = hasPermission("email-template-list", featurePermissions)
  const canOpenEmailDesigner = emailTemplateListPermission || emailTemplateViewPermission || canSaveEmailTemplate
  // Editing an existing template requires view-or-edit — matches canOpenEmailDesigner's
  // intent for the edit route, used to gate the actual GET below.
  const canLoadEmailTemplateRecord = emailTemplateViewPermission || canSaveEmailTemplate

  // Load existing record when editing, reset the canvas when creating fresh.
  // loadedIdRef guards both branches so the body only actually runs once per
  // `id` — otherwise re-running this effect once permissions resolve (needed
  // so the edit path's deferred loadTemplate call can fire) would also
  // re-fire the create path's canvas reset a second time.
  const loadedIdRef = React.useRef(null)
  useEffect(() => {
    if (isEdit) {
      if (featurePermissionsLoading) return
      if (!canLoadEmailTemplateRecord) {
        // Permissions resolved but this user can't load the record — stop
        // spinning instead of leaving "Loading template…" up forever.
        setIsLoading(false)
        return
      }
      if (loadedIdRef.current === id) return
      loadedIdRef.current = id
      loadTemplate(id)
    } else {
      if (loadedIdRef.current === id) return
      loadedIdRef.current = id
      clearAll()
      setTemplateName('Untitled Email')
      setCurrentHtml('')
      savedHtmlRef.current = null      // nothing persisted yet → stays dirty
      awaitingBaselineRef.current = false
      setIsSaved(false)
      setLoadedIsTemplate(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, featurePermissionsLoading, canLoadEmailTemplateRecord])

  async function loadTemplate(templateId) {
    setIsLoading(true)
    try {
      const url = `${process.env.REACT_APP_API_URL}/email-template/get-by-id?id=${templateId}`
      const res = await fetchWrapper.get(url)
      const item = res?.data
      if (item) {
        setTemplateName(item.name || 'Untitled Email')
        setBlocks(decodeBlocksContent(item.blocks || []))
        // Replace, don't merge — see setEmailSettings in the store.
        setEmailSettings(item.email_settings)
        setCustomVariables(item.custom_variables || item.customVariables || [])
        setCurrentHtml(decodeHtmlEntities(item.html_code) || '')
        setLoadedIsTemplate(Boolean(item.is_template))
        // The designer's first emit lands after this and becomes the clean baseline.
        awaitingBaselineRef.current = true
        setIsSaved(true)
      }
    } catch (error) {
      toast({ title: 'Failed to load template', status: 'error', duration: 3000 })
    } finally {
      setIsLoading(false)
    }
  }

  function handleUseTemplate(item) {
    setBlocks(decodeBlocksContent(item.blocks || []))
    setEmailSettings(item.email_settings)
    setCustomVariables(item.custom_variables || item.customVariables || [])
    setCurrentHtml(decodeHtmlEntities(item.html_code) || '')
    // Applying a pre-built is genuinely unsaved work → no baseline.
    savedHtmlRef.current = null
    awaitingBaselineRef.current = false
    setIsSaved(false)
    toast({ title: 'Template loaded', description: `"${item.name}" is now in your editor`, status: 'success', duration: 2500 })
  }

  /**
   * ET_040: the designer emits onChange as soon as it mounts, so a freshly opened
   * saved email was immediately marked dirty — "Unsaved changes" with no edit made.
   * Marking it clean in loadTemplate could not win, because that emit lands after.
   *
   * Instead compare against a baseline of what is actually persisted:
   *   - opening a saved record  -> the first emit BECOMES the baseline (clean)
   *   - applying a pre-built    -> baseline null, so it stays dirty (correct: unsaved)
   *   - saving                  -> baseline becomes the saved html
   * Any later emit that differs from the baseline is a genuine edit.
   */
  const savedHtmlRef = React.useRef(null)
  const awaitingBaselineRef = React.useRef(false)

  const handleHtmlChange = useCallback((html) => {
    setCurrentHtml(html)
    if (awaitingBaselineRef.current) {
      awaitingBaselineRef.current = false
      savedHtmlRef.current = html
      setIsSaved(true)
      return
    }
    setIsSaved(savedHtmlRef.current !== null && html === savedHtmlRef.current)
  }, [])

  function handleCopyHtml() {
    if (!currentHtml) {
      toast({ title: 'Nothing to copy', description: 'Add some blocks first', status: 'warning', duration: 2000 })
      return
    }
    navigator.clipboard.writeText(currentHtml).then(() => {
      toast({ title: 'Copied!', status: 'success', duration: 2000 })
    })
  }

  function handleDownloadHtml() {
    if (!currentHtml) {
      toast({ title: 'Nothing to download', description: 'Add some blocks first', status: 'warning', duration: 2000 })
      return
    }
    const blob = new Blob([currentHtml], { type: 'text/html;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${templateName.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'email'}.html`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast({ title: 'Downloaded!', status: 'success', duration: 2000 })
  }

  function buildSavePayload(saveType) {
    const { variables, customVariables } = getUsedVariables()
    return {
      name: templateName,
      save_type: saveType,
      html_code: currentHtml,
      // AMP part (text/x-amp-html), persisted so the backend can send it.
      //
      // It must go out as a SEPARATE part inside multipart/alternative, never as the
      // whole body: an AMP4EMAIL document carries
      // `<style amp4email-boilerplate>body{visibility:hidden}</style>`, which only the
      // AMP runtime undoes, and mail clients strip <script>. Used as the sole body it
      // renders blank in Outlook, Apple Mail and Gmail without AMP. Gmail also
      // requires AMP sender registration before it will honour the part at all.
      //
      // Story auto-play (ET_022) and interactive form submission (ET_025) exist only
      // in this part; the HTML part is a static fallback by design.
      amp_code: blocksToAmpEmail(blocks, emailSettings),
      email_type: 'html_code',
      blocks,
      email_settings: emailSettings,
      variables,
      custom_variables: customVariables,
      savedAt: new Date().toISOString(),
      is_template: saveType === 'template',
    }
  }

  async function persist(saveType) {
    if (!templateName || !templateName.trim()) {
      toast({ title: 'Name required', description: 'Email name is required', status: 'warning', duration: 2500 })
      return
    }
    if (!currentHtml || blocks.length === 0) {
      toast({ title: 'Nothing to save', description: 'Add some blocks first', status: 'warning', duration: 2000 })
      return
    }
    if (hasEmptyButtonLabel(blocks)) {
      toast({ title: 'Button label required', description: 'Every button block needs a label before saving.', status: 'warning', duration: 3000 })
      return
    }
    setIsSaving(true)
    try {
      const payload = buildSavePayload(saveType)
      const url = isEdit
        ? `${process.env.REACT_APP_API_URL}/email-template/update`
        : `${process.env.REACT_APP_API_URL}/email-template/create`
      const body = isEdit ? { id: Number(id), ...payload } : payload
      const res = await fetchWrapper.post(url, body)

      if (res?.success === false) {
        toast({ title: 'Save failed', description: res?.message || 'Please try again', status: 'error', duration: 3000 })
        return
      }

      // What is now persisted becomes the clean baseline.
      savedHtmlRef.current = currentHtml
      awaitingBaselineRef.current = false
      setIsSaved(true)
      toast({
        title: saveType === 'template' ? 'Template Saved!' : 'Email Saved!',
        description: `"${templateName}" ${isEdit ? 'updated' : 'saved'}`,
        status: 'success',
        duration: 2500,
      })

      // First save of a new record — switch into edit mode using the new id
      if (!isEdit) {
        const newId = res?.data?.id
        if (newId) navigate(`/tools/email-designer/${newId}`, { replace: true })
      }
    } catch (error) {
      toast({ title: 'Save failed', description: 'Please try again', status: 'error', duration: 3000 })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveEmail = () => persist('email')
  const handleSaveTemplate = () => persist('template')

  if (featurePermissionsLoading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        </BoxWrapper>
      </>
    )
  }

  if (!canOpenEmailDesigner) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                No Access
              </Text>
              <Text fontSize="sm" color="gray.500">
                You don't have permission to view this sections.
              </Text>
            </Box>
          </Center>
        </BoxWrapper>
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Flex align="center" justify="center" h="60vh" direction="column" gap={3}>
            <Spinner size="lg" color={ACCENT} thickness="3px" />
            <Text fontSize="13px" color="gray.500">Loading template…</Text>
          </Flex>
        </BoxWrapper>
      </>
    )
  }

  return (
    <>
      <Header />
      <BoxWrapper>
        {/* ── Compact page toolbar ── */}
        <Flex
          align="center" px={1} mb={3} pb={3}
          borderBottom="1px solid" borderColor="gray.100"
          gap={3}
        >
          <Tooltip label="Back to templates" hasArrow placement="bottom">
            <IconButton
              aria-label="Back to templates"
              icon={<ArrowLeft size={15} />}
              size="sm" variant="ghost"
              onClick={() => navigate('/tools/email-templates')}
            />
          </Tooltip>

          {/* Icon + name */}
          <Flex align="center" gap={2.5} minW={0} flex="0 0 auto">
            <Box
              w={8} h={8} borderRadius="9px"
              style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, var(--brand-primary-hover) 100%)` }}
              display="flex" alignItems="center" justifyContent="center"
              flexShrink={0}
              shadow={`0 2px 8px ${ACCENT}55`}
            >
              <Mail size={14} color="white" />
            </Box>
            <Box>
              <Flex align="center" gap={2}>
                <Input
                  value={templateName}
                  onChange={e => { setTemplateName(e.target.value); setIsSaved(false) }}
                  variant="unstyled"
                  fontSize="14px"
                  fontWeight="700"
                  color="gray.800"
                  w="180px"
                  placeholder="Template name…"
                  _placeholder={{ color: 'gray.300' }}
                />
                <FileText size={11} color="#d1d5db" />
              </Flex>
              <Text fontSize="10px" color="gray.400" lineHeight={1} mt="1px">
                {isEdit ? 'Editing' : 'New'} Email Template
              </Text>
            </Box>
          </Flex>

          {/* Separator */}
          <Box h="24px" w="1px" bg="gray.150" flexShrink={0} />

          {/* Save-state indicator + block count. Honest dirty/saved state —
              saving is manual (Save Email / Save Template), no auto-save. */}
          <Flex align="center" gap={2} flexShrink={0}>
            <Flex align="center" gap={1.5}>
              <Box
                w={2} h={2} borderRadius="full"
                bg={isSaved ? '#10b981' : '#f59e0b'}
                transition="background 0.3s"
              />
              <Text fontSize="10px" color={isSaved ? '#10b981' : '#b45309'} fontWeight="600">
                {isSaved ? 'Saved' : 'Unsaved changes'}
              </Text>
            </Flex>
            <Box h="12px" w="1px" bg="gray.200" />
            <Badge
              fontSize="10px" px={2} py={0.5} borderRadius="5px"
              style={{
                background: blocks.length > 0 ? `${ACCENT}15` : '#f1f5f9',
                color: blocks.length > 0 ? ACCENT : '#94a3b8',
                border: `1px solid ${blocks.length > 0 ? ACCENT + '30' : '#e2e8f0'}`,
              }}
            >
              {blocks.length} block{blocks.length !== 1 ? 's' : ''}
            </Badge>
          </Flex>

          <Box flex={1} />

          {/* Actions */}
          <HStack spacing={1.5}>
            <Tooltip label="Browse saved templates" hasArrow placement="bottom">
              <Button
                size="sm" variant="ghost" h="32px" px={3}
                fontSize="12px" color="gray.500" fontWeight="600"
                leftIcon={<LayoutTemplate size={13} />}
                _hover={{ bg: 'gray.100', color: 'gray.700' }}
                onClick={openTemplates}
              >
                Templates
              </Button>
            </Tooltip>

            <Tooltip label="Full preview" hasArrow placement="bottom">
              <Button
                size="sm" variant="ghost" h="32px" px={3}
                fontSize="12px" color="gray.500" fontWeight="600"
                leftIcon={<Eye size={13} />}
                _hover={{ bg: 'gray.100', color: 'gray.700' }}
                onClick={openPreview}
              >
                Preview
              </Button>
            </Tooltip>

            <Tooltip label="Copy HTML to clipboard" hasArrow placement="bottom">
              <Button
                size="sm" variant="ghost" h="32px" px={3}
                fontSize="12px" color="gray.500" fontWeight="600"
                leftIcon={<Copy size={13} />}
                _hover={{ bg: 'gray.100', color: 'gray.700' }}
                onClick={handleCopyHtml}
                isDisabled={!canSaveEmailTemplate}
              >
                Copy HTML
              </Button>
            </Tooltip>

            <Tooltip label="Download .html file" hasArrow placement="bottom">
              <Button
                size="sm" variant="ghost" h="32px" px={3}
                fontSize="12px" color="gray.500" fontWeight="600"
                leftIcon={<Download size={13} />}
                _hover={{ bg: 'gray.100', color: 'gray.700' }}
                onClick={handleDownloadHtml}
                isDisabled={!canSaveEmailTemplate}
              >
                Export
              </Button>
            </Tooltip>

            <Box h="18px" w="1px" bg="gray.200" />

            <Tooltip label="Run deliverability & accessibility checks" hasArrow placement="bottom">
              <Button
                size="sm" variant="ghost" h="32px" px={3}
                fontSize="12px" color="gray.500" fontWeight="600"
                leftIcon={<ShieldCheck size={13} />}
                _hover={{ bg: 'gray.100', color: 'gray.700' }}
                onClick={openLint}
              >
                Check
              </Button>
            </Tooltip>

            <Tooltip label="Send this email to test addresses" hasArrow placement="bottom">
              <Button
                size="sm" h="32px" px={3.5}
                fontSize="12px" fontWeight="700"
                leftIcon={<Mail size={12} />}
                variant="outline"
                style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}
                _hover={{ bg: 'rgb(var(--brand-primary-rgb) / 0.063)' }}
                onClick={openTestEmail}
                isDisabled={!canSaveEmailTemplate}
              >
                Send Test Email
              </Button>
            </Tooltip>

            <EmailLintModal isOpen={lintOpen} onClose={closeLint}
              blocks={blocks} html={currentHtml} settings={emailSettings} subject={templateName} />

            {!(isEdit && loadedIsTemplate) && canSaveEmailTemplate && (
              <Tooltip label="Save as a standalone email" hasArrow placement="bottom">
                <Button
                  size="sm" h="32px" px={3.5}
                  fontSize="12px" fontWeight="700"
                  leftIcon={<Save size={12} />}
                  variant="outline"
                  isLoading={isSaving}
                  style={{ borderColor: ACCENT, color: ACCENT }}
                  _hover={{ bg: `${ACCENT}10` }}
                  onClick={handleSaveEmail}
                >
                  Save Email
                </Button>
              </Tooltip>
            )}

            <SendTestEmailModal isOpen={testEmailOpen} onClose={closeTestEmail}
              html={currentHtml} amp={blocksToAmpEmail(blocks, emailSettings)} subject={templateName} />

            {canSaveEmailTemplate && (
              <Tooltip label="Save as a reusable template" hasArrow placement="bottom">
                <Button
                  size="sm" h="32px" px={3.5}
                  fontSize="12px" fontWeight="700"
                  leftIcon={<FileText size={12} />}
                  isLoading={isSaving}
                  style={{ background: ACCENT, color: 'white', border: 'none' }}
                  _hover={{ opacity: 0.9, boxShadow: `0 4px 14px ${ACCENT}55` }}
                  _active={{ opacity: 0.85 }}
                  onClick={handleSaveTemplate}
                >
                  Save Template
                </Button>
              </Tooltip>
            )}

            {!canSaveEmailTemplate && (
              <Badge
                fontSize="10px" px={2} py={0.5} borderRadius="5px"
                variant="outline" colorScheme="gray"
              >
                View only
              </Badge>
            )}
          </HStack>
        </Flex>

        {/* ── Designer canvas — maximised height ── */}
        <Box
          h="calc(100vh - 230px)"
          minH="480px"
          overflow="hidden"
          borderRadius="12px"
          border="1px solid"
          borderColor="gray.200"
          shadow="0 4px 24px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04)"
        >
          <EmailDesigner onChange={handleHtmlChange} canSave={canSaveEmailTemplate} />
        </Box>

        <PreviewModal isOpen={previewOpen} onClose={closePreview} html={currentHtml} />
        <TemplatesLibraryModal isOpen={templatesOpen} onClose={closeTemplates} onUse={handleUseTemplate} />
      </BoxWrapper>
    </>
  )
}

export default EmailDesignerPage
